/**
 * <sidequest-plan-selector>
 * ─────────────────────────
 * The custom Mail Club plan cards. Dependency-free; no libraries, no network.
 *
 * What it does, and why it does it that way:
 *
 *   · The cards are rendered by Liquid from Shopify's live selling-plan
 *     allocations, already pre-selected server-side, so the first paint is
 *     correct before any script runs. This file only (1) verifies the render,
 *     (2) keeps one hidden <input name="selling_plan"> on the product form in
 *     step with the chosen card, (3) mirrors that choice into Appstle's own
 *     widget, and (4) re-renders itself when Dawn swaps variants.
 *
 *   · Appstle stays the subscription engine. Its storefront script, with
 *     "Add.js interceptor" on (as it is on this store), rewrites every
 *     /cart/add, /wallets/checkouts.json and Storefront cartCreate request to
 *     carry whatever *its own* widget has checked — and blanks the plan when it
 *     has nothing checked. So the widget is never removed: it is kept in the
 *     form, driven to the same plan the customer picked here, verified, and
 *     only then hidden with CSS. Removing it (or using Appstle's setHidden API,
 *     which deletes the DOM) would make the interceptor strip the plan.
 *
 *   · Fallback is the default. The element is rendered `hidden` and only shown
 *     once the product form and at least one valid plan are found; if mirroring
 *     into Appstle cannot be verified, this selector hides itself again, the
 *     Appstle widget is shown, and its inputs are restored. No timeouts decide
 *     anything: readiness comes from the DOM, from Appstle's documented
 *     `AppstleSubscription:SubscriptionWidget:*` events, and from a mutation
 *     observer on the form as a version-proof safety net.
 *
 *   · The visible radios use a private name. Radios with the same name and the
 *     same form owner form one group; sharing `selling_plan` with Appstle's
 *     radios would make each click uncheck the other set.
 */

(function () {
  'use strict';

  if (window.SidequestPlanSelector) return;

  var TAG = 'sidequest-plan-selector';
  var ACTIVE_CLASS = 'sq-plan-selector-active';
  var DETACHED_FORM_ID = 'sq-plan-selector-detached';
  var APPSTLE_WIDGET = '.appstle_sub_widget';
  var APPSTLE_INPUT = 'input[name="selling_plan"]';
  var APPSTLE_EVENTS = ['widgetInitialised', 'SubscriptionWidgetUpdated', 'sellingPlanChanged', 'SellingPlanSelected'].map(
    function (name) {
      return 'AppstleSubscription:SubscriptionWidget:' + name;
    }
  );

  var SidequestPlanSelector = class extends HTMLElement {
    constructor() {
      super();
      this.syncing = false;
      this.syncQueued = false;
      this.observer = null;
      this.unsubscribeVariantChange = null;

      this.onChange = this.onChange.bind(this);
      this.onAppstleEvent = this.onAppstleEvent.bind(this);
      this.onFormMutation = this.onFormMutation.bind(this);
      this.onVariantChange = this.onVariantChange.bind(this);
    }

    connectedCallback() {
      this.addEventListener('change', this.onChange);

      APPSTLE_EVENTS.forEach(
        function (name) {
          document.addEventListener(name, this.onAppstleEvent);
        }.bind(this)
      );
      window.addEventListener('appstle:subscription-widget:loaded', this.onAppstleEvent);

      /* Dawn declares PUB_SUB_EVENTS with `const`, so it is a global binding
         but not a window property — read it as a bare identifier. */
      var events = typeof PUB_SUB_EVENTS !== 'undefined' ? PUB_SUB_EVENTS : window.PUB_SUB_EVENTS;
      if (typeof subscribe === 'function' && events && events.variantChange) {
        this.unsubscribeVariantChange = subscribe(events.variantChange, this.onVariantChange);
      }

      this.init();
    }

    disconnectedCallback() {
      this.removeEventListener('change', this.onChange);
      APPSTLE_EVENTS.forEach(
        function (name) {
          document.removeEventListener(name, this.onAppstleEvent);
        }.bind(this)
      );
      window.removeEventListener('appstle:subscription-widget:loaded', this.onAppstleEvent);
      if (this.unsubscribeVariantChange) this.unsubscribeVariantChange();
      this.stopObservingForm();
    }

    /* ── accessors ─────────────────────────────────────────────────────── */

    get form() {
      return document.getElementById(this.dataset.formId || '');
    }

    /** The product section, so lookups and the hide class cover Appstle placements outside the <form>. */
    get root() {
      return this.closest('product-info') || this.form;
    }

    findAppstleWidget(form) {
      var root = this.root;
      return (
        (form && form.querySelector(APPSTLE_WIDGET)) ||
        (root && root.querySelector(APPSTLE_WIDGET)) ||
        document.querySelector(APPSTLE_WIDGET)
      );
    }

    setActive(active) {
      var form = this.form;
      var root = this.root;
      [form, root].forEach(function (node) {
        if (node) node.classList.toggle(ACTIVE_CLASS, active);
      });
    }

    get valueInput() {
      return this.querySelector('[data-sq-plan-value]');
    }

    get radios() {
      return Array.prototype.slice.call(this.querySelectorAll('input[type="radio"]'));
    }

    get selectedRadio() {
      return this.radios.filter(function (radio) {
        return radio.checked;
      })[0] || null;
    }

    get selectedId() {
      var radio = this.selectedRadio;
      return radio ? radio.value : '';
    }

    /* ── lifecycle ─────────────────────────────────────────────────────── */

    /** Verify the render, settle the selection and go live — or step back. */
    init() {
      var form = this.form;
      var radios = this.radios.filter(function (radio) {
        return /^\d+$/.test(radio.value);
      });

      if (!form || this.dataset.plansAvailable !== 'true' || !radios.length || !this.valueInput) {
        this.fallback(!form ? 'product form not found' : 'no selling plans on this variant');
        return;
      }

      if (!this.selectedRadio) this.selectDefault(radios);

      this.valueInput.disabled = false;
      this.writeValue();
      this.updateCardState();

      this.dataset.state = 'ready';
      this.hidden = false;
      this.setActive(true);

      this.observeForm(this.root || form);
      this.syncAppstle();
    }

    /** The block setting's deliveries-per-renewal wins, else the first plan. */
    selectDefault(radios) {
      var wanted = parseInt(this.dataset.defaultDeliveries, 10) || 3;
      var pick =
        radios.filter(function (radio) {
          return parseInt(radio.dataset.deliveries, 10) === wanted;
        })[0] || radios[0];
      pick.checked = true;
    }

    writeValue() {
      var input = this.valueInput;
      if (!input) return;
      var id = this.selectedId;
      if (input.value !== id) {
        input.value = id;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    updateCardState() {
      Array.prototype.forEach.call(
        this.querySelectorAll('.sq-ps__card'),
        function (card) {
          var radio = card.querySelector('input[type="radio"]');
          card.classList.toggle('is-selected', !!(radio && radio.checked));
        }
      );
    }

    onChange(event) {
      var target = event.target;
      if (!target || target.type !== 'radio' || !this.contains(target)) return;
      this.writeValue();
      this.updateCardState();
      this.syncAppstle();
      this.dispatchEvent(
        new CustomEvent('sidequest:plan-change', {
          bubbles: true,
          detail: { sellingPlanId: this.selectedId, deliveries: parseInt(target.dataset.deliveries, 10) || 1 },
        })
      );
    }

    /**
     * Dawn re-fetches the section on a variant change and publishes the parsed
     * HTML. Swap in the freshly rendered cards (correct allocations and money
     * format for the new variant), keep the customer's plan if it still exists,
     * then run init() again so the ready/fallback decision is re-made.
     */
    onVariantChange(payload) {
      var data = payload && payload.data;
      if (!data || data.sectionId !== this.dataset.sectionId || !data.html) return;

      var fresh = data.html.getElementById(this.id);
      if (!fresh) return;

      var previous = this.selectedId;
      this.dataset.plansAvailable = fresh.dataset.plansAvailable || 'false';
      this.innerHTML = fresh.innerHTML;

      var keep = this.radios.filter(function (radio) {
        return radio.value === previous;
      })[0];
      if (keep) {
        this.radios.forEach(function (radio) {
          radio.checked = radio === keep;
        });
      }

      this.init();
    }

    /* ── Appstle ───────────────────────────────────────────────────────── */

    onAppstleEvent() {
      if (this.syncing || this.dataset.state !== 'ready') return;
      this.queueSync();
    }

    onFormMutation() {
      if (this.syncing || this.dataset.state !== 'ready') return;
      this.queueSync();
    }

    /** Coalesce bursts of Appstle events into one sync per frame. */
    queueSync() {
      if (this.syncQueued) return;
      this.syncQueued = true;
      window.requestAnimationFrame(
        function () {
          this.syncQueued = false;
          this.syncAppstle();
        }.bind(this)
      );
    }

    observeForm(form) {
      if (this.observer || !('MutationObserver' in window)) return;
      this.observer = new MutationObserver(this.onFormMutation);
      this.observer.observe(form, { childList: true, subtree: true });
    }

    stopObservingForm() {
      if (!this.observer) return;
      this.observer.disconnect();
      this.observer = null;
    }

    /**
     * Mirror the chosen plan into Appstle's widget. Nothing to do until the
     * widget exists; when it does, drive its radios through their own change
     * handlers (so Appstle's internal state follows), verify, and keep its
     * inputs out of the form's submission so the plan is submitted once.
     */
    syncAppstle() {
      if (this.dataset.state !== 'ready') return;
      var form = this.form;
      if (!form) return;

      var widget = this.findAppstleWidget(form);
      if (!widget) {
        this.setActive(true);
        return;
      }

      var id = this.selectedId;
      if (!id) return;

      this.syncing = true;
      var ok = false;
      try {
        ok = this.driveAppstle(widget, id);
      } catch (error) {
        ok = false;
      } finally {
        this.syncing = false;
      }

      if (ok) {
        this.detachAppstleInputs(widget);
        window.appstleSelectedSellingPlan = id;
        this.setActive(true);
      } else {
        this.fallback('could not mirror the selection into the Appstle widget', widget);
      }
    }

    driveAppstle(widget, id) {
      var current = function () {
        var checked = widget.querySelector(APPSTLE_INPUT + ':checked');
        return checked ? checked.value : '';
      };
      if (current() === id) return true;

      var tops = Array.prototype.slice.call(widget.querySelectorAll(APPSTLE_INPUT));
      var inners = Array.prototype.slice.call(widget.querySelectorAll('input[type="radio"]:not([name="selling_plan"])'));

      var inner = inners.filter(function (radio) {
        return radio.value === id;
      })[0];
      var top = tops.filter(function (radio) {
        return radio.value === id || radio.getAttribute('data-appstle-real-value') === id;
      })[0];

      /* A prepaid group shares one top-level radio; find it from the inner one. */
      if (!top && inner) {
        var node = inner.parentElement;
        while (node && node !== widget && !top) {
          top = node.querySelector(APPSTLE_INPUT);
          node = node.parentElement;
        }
      }
      if (!top && !inner) return false;

      /* Top first (activates the group), then the plan inside it: Appstle
         copies the inner plan id into the active top-level radio on change. */
      if (top && !top.checked) top.click();
      if (inner && !inner.checked) inner.click();

      if (current() !== id) {
        var checked = widget.querySelector(APPSTLE_INPUT + ':checked') || top;
        if (!checked) return false;
        checked.checked = true;
        checked.value = id;
      }
      return current() === id;
    }

    /**
     * Give Appstle's radios a form attribute that points nowhere. They stay in
     * the DOM, enabled and checked (Appstle's interceptor and handlers read
     * them there), but they no longer belong to the product form, so the form
     * submits a single selling_plan: ours.
     */
    detachAppstleInputs(widget) {
      Array.prototype.forEach.call(widget.querySelectorAll(APPSTLE_INPUT), function (input) {
        if (input.getAttribute('form') !== DETACHED_FORM_ID) input.setAttribute('form', DETACHED_FORM_ID);
      });
    }

    restoreAppstleInputs(widget) {
      if (!widget) return;
      Array.prototype.forEach.call(widget.querySelectorAll(APPSTLE_INPUT), function (input) {
        if (input.getAttribute('form') === DETACHED_FORM_ID) input.removeAttribute('form');
      });
    }

    /** Step back: show Appstle's widget, submit nothing of our own. */
    fallback(reason, widget) {
      this.setActive(false);
      this.restoreAppstleInputs(widget || this.findAppstleWidget(this.form));
      if (this.valueInput) this.valueInput.disabled = true;
      this.hidden = true;
      this.dataset.state = 'fallback';
      if (window.console && reason) {
        console.warn('[' + TAG + '] using the Appstle widget instead: ' + reason);
      }
    }
  };

  if (!customElements.get(TAG)) {
    customElements.define(TAG, SidequestPlanSelector);
  }

  window.SidequestPlanSelector = true;
})();
